/**
 * useEntityCosts - TCO d'une entité (véhicule / conducteur) pour l'onglet Coûts.
 *
 * SOURCE UNIQUE DES MONTANTS : le moteur KPI backend (`GET /kpis/costs/by-vehicle`,
 * `backend/services/kpi_service.mjs`) — les 8 familles de coûts (carburant, consommables,
 * maintenance, assurance, leasing, sinistres, taxes, autres) y sont calculées une seule fois,
 * pour le cockpit, les rapports, le budget et cette fiche. Ce hook ne recalcule RIEN :
 * jusqu'au 2026-08-21 il additionnait lui-même 5 familles à partir des entités brutes
 * (prorata d'assurance, fenêtre calendaire, leasing ignoré, devise EUR figée) et la fiche
 * véhicule affichait un TCO différent du rapport et du cockpit — silo rabattu par
 * `backend/scripts/test_tco_reconciliation.mjs`.
 *
 * Les entités brutes (`rawData`) ne servent qu'au drill-down d'audit (liste des pleins,
 * interventions, …) ; elles sont filtrées sur la même fenêtre glissante que le moteur.
 *
 * Conducteur : le moteur n'expose pas de coûts par conducteur — seules les dépenses rattachées
 * au conducteur sont listées (pas de TCO moteur, signalé par `costs.source === 'expenses'`).
 *
 * @param {string} entityType - 'vehicle' | 'driver'
 * @param {number|string} entityId
 * @param {string} initialPeriod - 'month' | 'quarter' | 'year' (périodes du moteur KPI)
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api.js';

// Familles du moteur KPI (ordre d'affichage) — cf. COST_FAMILIES dans kpi_service.mjs
export const COST_FAMILIES = ['fuel', 'consumable', 'maintenance', 'insurance', 'leasing', 'claims', 'tax', 'other'];

export const COST_CATEGORIES = {
  fuel:        { label: 'Carburant',    icon: 'Fuel',          color: 'orange' },
  consumable:  { label: 'Consommables', icon: 'Droplets',      color: 'cyan' },
  maintenance: { label: 'Maintenance',  icon: 'Wrench',        color: 'amber' },
  insurance:   { label: 'Assurance',    icon: 'Shield',        color: 'blue' },
  leasing:     { label: 'Leasing',      icon: 'FileText',      color: 'violet' },
  claims:      { label: 'Sinistres',    icon: 'AlertTriangle', color: 'red' },
  tax:         { label: 'Taxes',        icon: 'Landmark',      color: 'emerald' },
  other:       { label: 'Autres',       icon: 'Receipt',       color: 'slate' }
};

export const PERIODS = [
  { id: 'month',   label: '30 jours', days: 30 },
  { id: 'quarter', label: '90 jours', days: 90 },
  { id: 'year',    label: '12 mois',  days: 365 }
];

const CONSUMABLE_TYPES = ['adblue', 'engine_oil', 'brake_fluid', 'coolant', 'washer_fluid'];

// Même fenêtre que le moteur : date('now', '-N day')
function getPeriodStartDate(period) {
  const days = (PERIODS.find(p => p.id === period) || PERIODS[0]).days;
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d;
}

function isWithinPeriod(dateStr, periodStart) {
  if (!dateStr) return false;
  return new Date(dateStr) >= periodStart;
}

const num = (x) => {
  const n = parseFloat(x);
  return Number.isFinite(n) ? n : 0;
};

const EMPTY_RAW = { fuelLogs: [], maintenance: [], insurance: [], claims: [], expenses: [] };

export function useEntityCosts(entityType, entityId, initialPeriod = 'month') {
  const [period, setPeriod] = useState(initialPeriod);
  const [kpiRow, setKpiRow] = useState(null);     // ligne du moteur pour ce véhicule
  const [rawData, setRawData] = useState(EMPTY_RAW);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchCosts = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    setError(null);

    try {
      if (entityType === 'vehicle') {
        const safe = (p) => (p || Promise.resolve({ data: [] })).catch(() => ({ data: [] }));
        const [kpiRes, fuelRes, maintenanceRes, insuranceRes, contractsRes, claimsRes, expensesRes] = await Promise.all([
          api.kpi.getCostsByVehicle(period),
          safe(api.fuelLog?.getAll?.()),
          safe(api.maintenance?.getAll?.()),
          safe(api.vehicleInsurance?.getAll?.()),
          safe(api.insurance?.getAll?.()),
          safe(api.claim?.getAll?.()),
          safe(api.expense?.getAll?.())
        ]);

        const vehicles = kpiRes?.data?.vehicles || [];
        setKpiRow(vehicles.find(v => String(v.vehicle_id) === String(entityId)) || null);

        const contracts = contractsRes.data || [];
        const enrichedInsurance = (insuranceRes.data || [])
          .filter(vi => vi.vehicle_id === entityId && !vi.removed_at)
          .map(vi => {
            const contract = contracts.find(c => c.id === vi.contract_id);
            return { ...vi, _policy_number: contract?.policy_number, _insurer_name: contract?.insurer_name };
          });

        setRawData({
          fuelLogs: (fuelRes.data || []).filter(f => f.vehicle_id === entityId),
          maintenance: (maintenanceRes.data || []).filter(m => m.vehicle_id === entityId),
          insurance: enrichedInsurance,
          claims: (claimsRes.data || []).filter(c => c.vehicle_id === entityId),
          expenses: (expensesRes.data || []).filter(e => e.vehicle_id === entityId)
        });
      } else if (entityType === 'driver') {
        const expensesRes = await (api.expense?.getAll?.() || Promise.resolve({ data: [] })).catch(() => ({ data: [] }));
        setKpiRow(null);
        setRawData({ ...EMPTY_RAW, expenses: (expensesRes.data || []).filter(e => e.driver_id === entityId) });
      }
    } catch (err) {
      console.error('Error fetching entity costs:', err);
      setError(err.message || 'Impossible de charger les coûts');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, period]);

  useEffect(() => { fetchCosts(); }, [fetchCosts]);

  const costs = useMemo(() => {
    const periodStart = getPeriodStartDate(period);
    const periodLabel = (PERIODS.find(p => p.id === period) || PERIODS[0]).label;

    // Listes d'audit, sur la fenêtre du moteur
    const filteredFuel = rawData.fuelLogs.filter(f => isWithinPeriod(f.fuel_date, periodStart));
    const fuelOnly = filteredFuel.filter(f => !CONSUMABLE_TYPES.includes(f.fuel_type));
    const consumables = filteredFuel.filter(f => CONSUMABLE_TYPES.includes(f.fuel_type));
    const filteredMaintenance = rawData.maintenance.filter(m => isWithinPeriod(m.completed_date || m.completed_at, periodStart));
    const filteredClaims = rawData.claims.filter(c => isWithinPeriod(c.event_date, periodStart));
    const filteredExpenses = rawData.expenses.filter(e => isWithinPeriod(e.invoice_date || e.expense_date, periodStart));

    // Montants : le moteur KPI fait foi. Conducteur : somme des dépenses rattachées (pas de moteur).
    const fromEngine = entityType === 'vehicle';
    const b = kpiRow?.breakdown || {};
    const totals = Object.fromEntries(COST_FAMILIES.map(f => [f, fromEngine ? num(b[f]) : 0]));
    if (!fromEngine) {
      totals.tax = filteredExpenses.filter(e => e.category === 'tax').reduce((s, e) => s + num(e.gross_amount), 0);
      totals.other = filteredExpenses.filter(e => e.category !== 'tax').reduce((s, e) => s + num(e.gross_amount), 0);
    }
    const tco = fromEngine ? num(kpiRow?.total) : COST_FAMILIES.reduce((s, f) => s + totals[f], 0);
    const pct = (v) => (tco > 0 ? (v / tco) * 100 : 0);

    const fuelLiters = fuelOnly.reduce((s, f) => s + num(f.liters), 0);
    const claimGross = (c) => num(c.montant_facture_garage) || num(c.montant_expertise) || num(c.estimated_amount);
    const maintPreventive = Math.min(
      filteredMaintenance
        .filter(m => ['oil_change', 'tire_rotation', 'inspection'].includes(m.maintenance_type))
        .reduce((s, m) => s + (num(m.actual_total) || num(m.total_cost) || num(m.estimated_cost)), 0),
      totals.maintenance
    );
    const byCategory = {};
    for (const e of filteredExpenses) {
      if (e.category === 'tax') continue;
      byCategory[e.category || 'other'] = (byCategory[e.category || 'other'] || 0) + num(e.gross_amount);
    }

    return {
      tco,
      total_ref: fromEngine ? num(kpiRow?.total_ref) : tco,
      currency: kpiRow?.currency || 'EUR',
      source: fromEngine ? 'kpi' : 'expenses',
      breakdown: {
        fuel: { total: totals.fuel, percentage: pct(totals.fuel), liters: fuelLiters, avgPrice: fuelLiters > 0 ? totals.fuel / fuelLiters : 0, count: fuelOnly.length },
        consumable: { total: totals.consumable, percentage: pct(totals.consumable), count: consumables.length },
        maintenance: {
          total: totals.maintenance, percentage: pct(totals.maintenance), count: filteredMaintenance.length,
          // Répartition indicative (types préventifs connus) ; le total reste celui du moteur
          preventive: maintPreventive,
          corrective: Math.max(totals.maintenance - maintPreventive, 0)
        },
        insurance: {
          total: totals.insurance, percentage: pct(totals.insurance),
          annualPremium: rawData.insurance.reduce((s, vi) => s + num(vi.premium_amount), 0),
          contracts: rawData.insurance.length
        },
        leasing: { total: totals.leasing, percentage: pct(totals.leasing), count: totals.leasing > 0 ? 1 : 0 },
        claims: {
          total: totals.claims, percentage: pct(totals.claims),
          grossCost: filteredClaims.reduce((s, c) => s + claimGross(c), 0),
          indemnified: filteredClaims.reduce((s, c) => s + num(c.indemnity_amount), 0),
          count: filteredClaims.length
        },
        tax: { total: totals.tax, percentage: pct(totals.tax), count: filteredExpenses.filter(e => e.category === 'tax').length },
        other: { total: totals.other, percentage: pct(totals.other), byCategory, count: filteredExpenses.filter(e => e.category !== 'tax').length }
      },
      period,
      periodLabel
    };
  }, [kpiRow, rawData, period, entityType]);

  return {
    costs,
    rawData,
    loading,
    error,
    period,
    setPeriod,
    reload: fetchCosts
  };
}

export default useEntityCosts;
